import { useAsyncData } from 'nuxt/app';
import {
  computed,
  onScopeDispose,
  ref,
  shallowRef,
  toValue,
  watch,
  type MaybeRefOrGetter,
} from 'vue';
import { useApiModel } from '~/composables/useApiModel';
import type { Campaign } from '~/types/Candidates';
import type { ApiError } from '~/types/plugins/api';
import { SEARCH_DEBOUNCE_MS } from '~/utils/search';

interface ApiCampaign {
  id: number;
  title: string;
  slug: string;
  status: 'active' | 'inactive';
  type: 'community' | 'party';
  description: string | null;
}

function mapCampaign(raw: ApiCampaign): Campaign {
  return {
    id: raw.slug,
    title: raw.title,
    type: raw.type,
    description: raw.description ?? '',
    candidateIds: [],
    tags: [],
    // Not eager-loaded on the list endpoint — only the show endpoint returns
    // per-entity sources (see useCampaign.ts).
    sources: [],
  };
}

export interface UseCampaignsOptions {
  query?: MaybeRefOrGetter<string>;
  sortOrder?: MaybeRefOrGetter<'latest' | 'oldest'>;
  typeFilter?: MaybeRefOrGetter<'all' | 'community' | 'party'>;
  /**
   * When set, scopes results to campaigns relevant to that suburb — matches
   * useParties' suburbId param and useExploreCounts' filter[suburb_id], so
   * the Campaigns list agrees with the Home Explore count for the same
   * selected location instead of always returning the site-wide list.
   */
  suburbId?: MaybeRefOrGetter<string | null>;
  /**
   * Page size. Defaults to 9 (the campaigns list page's grid). Callers that
   * don't paginate (e.g. search.vue, which never calls loadMore) should pass
   * a larger bound instead — matching useParties' per_page=50 precedent —
   * so results aren't silently truncated to the first page.
   */
  perPage?: number;
  /**
   * Gates fetching entirely. Defaults to true. search.vue passes its
   * `hasQuery` computed here so visiting `/search` with an empty query issues
   * no request — while `enabled` is false, neither the initial fetch nor a
   * filter change triggers one. Flipping it back to true does not itself
   * trigger a fetch — the caller's filters need to actually change too,
   * which is exactly what happens once a query is typed.
   */
  enabled?: MaybeRefOrGetter<boolean>;
}

export function useCampaigns(options: UseCampaignsOptions = {}) {
  const {
    query,
    sortOrder,
    typeFilter,
    suburbId,
    perPage = 9,
    enabled = true,
  } = options;
  const page = ref(1);
  // Always wholesale-replaced or spread into a new array, never mutated in
  // place — shallowRef skips deep-proxying every Campaign object on each
  // reassignment since nothing reads/writes into them reactively.
  const accumulated = shallowRef<Campaign[]>([]);

  // Debounce the free-text query: the list is now server-filtered, so
  // binding it straight into `filters` would fire one API request per
  // keystroke (useApiModel's filter watcher runs load() on every filters
  // change) — the old mock-data version filtered client-side at zero cost.
  const debouncedQuery = ref(query ? toValue(query) : '');
  let queryTimer: ReturnType<typeof setTimeout> | undefined;
  watch(
    () => (query ? toValue(query) : ''),
    (value) => {
      clearTimeout(queryTimer);
      queryTimer = setTimeout(() => {
        debouncedQuery.value = value;
      }, SEARCH_DEBOUNCE_MS);
    },
  );
  onScopeDispose(() => clearTimeout(queryTimer));

  const filters = computed(() => {
    const params: Record<string, string> = {};
    const q = debouncedQuery.value.trim();
    if (q) params['filter[title]'] = q;

    const type = typeFilter ? toValue(typeFilter) : 'all';
    if (type && type !== 'all') params['filter[type]'] = type;

    const order = sortOrder ? toValue(sortOrder) : 'latest';
    params.sort = order === 'oldest' ? 'created_at' : '-created_at';

    // VTW-645: relies on API Campaign::scopeSuburbId (votewise-api!93) keeping
    // party-led campaigns visible statewide — deploy after that API change.
    const suburb = suburbId ? toValue(suburbId) : null;
    if (suburb) params['filter[suburb_id]'] = suburb;

    return params;
  });

  // `autoLoad: false` disables useApiModel's own internal filter/page/limit
  // watchers — we drive `load()` ourselves below. Letting both this
  // composable's filter-reset watcher AND useApiModel's own page watcher
  // react to the same change independently fired two concurrent, identical
  // requests whenever a filter change also reset the page (e.g. from 3 back
  // to 1) — guarding the page write alone doesn't fix this, since the write
  // still has to happen for the reset to take effect. Calling `load()`
  // exactly once per intended change is the only way to have one request
  // per change.
  const model = useApiModel<ApiCampaign>('campaigns', {
    filters,
    page,
    limit: ref(perPage),
    autoLoad: false,
  });

  // `error` is exposed via this local ref rather than `model.error` directly:
  // `model.error` never survives SSR->client hydration (it's a plain ref, not
  // part of the payload `useAsyncData` transfers below), so it would silently
  // read as `null` on the client even for a page that failed to load on the
  // server. The useAsyncData fetcher below rethrows `model.error` so the
  // failure lands in useAsyncData's OWN error — which does cross the payload —
  // and the `asyncError` watch seeds it back into this ref on the client.
  // Every later load (loadMore, filter changes) runs on whichever side the
  // user is on and keeps it in sync via `model.error` directly.
  const error = ref<ApiError | null>(null);
  watch(model.error, (value) => {
    error.value = value;
  });

  // Meta tracked separately from `model.meta` for the same reason: the
  // initial page's meta has to reach the client via the payload (see below),
  // and `model.meta` alone can't carry it across the hydration boundary.
  const currentMeta = ref<{ current_page?: number; last_page?: number }>({});

  // The key encodes every input of the fetch, not just the caller identity —
  // see useCandidates for the full story. In short: query changes remount the
  // page (fullPath page-key + debounced router.replace), and useAsyncData
  // skips the fetcher on remount whenever the key's cached status is already
  // "success", so a key that ignored the filters replayed stale data (or a
  // disabled mount's `null`) instead of fetching the new query. `enabled` is
  // in the key so a `null` stored while disabled never satisfies an enabled
  // mount for the same filters.
  const listKey = [
    'campaigns',
    perPage,
    toValue(enabled),
    toValue(query) ?? '',
    toValue(sortOrder) ?? '',
    toValue(typeFilter) ?? '',
    toValue(suburbId) ?? '',
  ].join(':');

  // `useAsyncData` wraps only the FIRST `load()` — the one that used to be a
  // fire-and-forget call Nuxt never awaited, rendering an empty grid on the
  // server and refetching the same page again after hydration. Every
  // subsequent load (loadMore, a filter change) still calls `model.load()`
  // directly below, exactly as before: those only ever happen client-side,
  // where there's no SSR gap to close and the existing accumulate/replace
  // logic already does the right thing.
  //
  // The fetcher hands back `model.items`/`model.meta` explicitly (rather than
  // relying on the model's own refs) because those refs are NOT part of what
  // `useAsyncData` transfers to the client — only its own `data` is. Without
  // this, the client's freshly-created `model.items` would sit at `[]`
  // forever post-hydration, contradicting what the server just rendered.
  const { data: initialData, error: asyncError } = useAsyncData<{
    items: ApiCampaign[];
    meta: { current_page?: number; last_page?: number };
  } | null>(
    listKey,
    async () => {
      if (!toValue(enabled)) return null;
      await model.load();
      // `model.load()` swallows its own exception into `model.error`, so a
      // failed load would otherwise resolve this fetcher to an empty-but-
      // successful result — hydrating into a false "no results" state with no
      // error anywhere. Rethrow so the failure crosses the hydration boundary
      // via useAsyncData's own error (Nuxt wraps it in a serializable
      // NuxtError; `model.error` itself is a plain ref the payload never
      // carries).
      if (model.error.value) throw model.error.value;
      const items = [...model.items.value];
      const meta = { ...model.meta.value };
      // Seed the display refs HERE, inside the awaited fetcher, not only via
      // the watch below — Vue watchers do not run during SSR, so the
      // immediate `watch(initialData, …)` fires once with `initialData`
      // still `null` and never re-fires when the fetch resolves, leaving
      // `accumulated` empty in the server HTML (and mismatching at
      // hydration). Matches useParties/useExploreCounts.
      accumulated.value = items.map(mapCampaign);
      currentMeta.value = meta;
      return { items, meta };
    },
    { default: () => null },
  );

  // Client-side path: covers hydration (payload fills `initialData` before
  // this immediate watch runs) and key-cached remounts where the fetcher
  // never executes; the in-fetcher seeding above covers the server render.
  watch(
    initialData,
    (result) => {
      if (!result) return;
      accumulated.value = result.items.map(mapCampaign);
      currentMeta.value = result.meta;
    },
    { immediate: true },
  );

  watch(
    asyncError,
    (value) => {
      if (value) error.value = value as ApiError;
    },
    { immediate: true },
  );

  // Any change to the filter set invalidates accumulated pages — restart
  // from page 1 and refetch. Guarded by `enabled` too: a filter change while
  // disabled (e.g. clearing the search box back to empty on search.vue) must
  // not slip through and fire an unscoped fetch.
  watch(filters, () => {
    if (!toValue(enabled)) return;
    accumulated.value = [];
    page.value = 1;
    model.load();
  });

  watch(
    () => model.items.value,
    (items) => {
      // Key the append/replace decision off the page the RESPONSE reports,
      // not the local `page` ref — a filter change can reset `page` to 1
      // before an in-flight page-2 request resolves, so the local ref no
      // longer reflects which page this particular response belongs to.
      const responsePage = model.meta.value.current_page ?? 1;
      // Drop a stale payload: the filter watcher reset `page` to 1 while an
      // older page-N request for the PREVIOUS filter set was still in
      // flight. useApiModel.load() has no request sequencing, so that late
      // response would otherwise land in `model.items` and get appended to
      // the freshly-reset list.
      if (responsePage !== 1 && responsePage !== page.value) return;
      currentMeta.value = model.meta.value;
      const mapped = items.map(mapCampaign);
      accumulated.value =
        responsePage === 1 ? mapped : [...accumulated.value, ...mapped];
    },
  );

  const hasMore = computed(() => {
    const current = currentMeta.value.current_page ?? 1;
    const last = currentMeta.value.last_page ?? 1;
    return current < last;
  });

  function loadMore() {
    // Error guard against the infinite-scroll request storm — see
    // useCandidates.loadMore for the full story (page advances on failure,
    // hasMore stays stale-true, the sentinel keeps firing).
    if (error.value || !hasMore.value || model.pending.value) return;
    page.value += 1;
    model.load();
  }

  const campaigns = computed(() => accumulated.value);

  return {
    campaigns,
    pending: model.pending,
    error,
    hasMore,
    loadMore,
  };
}
